import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    MdAccountBalanceWallet, MdAdd, MdEdit, MdDelete, MdWallet,
    MdTrendingUp, MdTrendingDown, MdFilterList, MdClose, MdSave,
    MdInfoOutline, MdCheckCircle, MdKeyboardArrowLeft, MdKeyboardArrowRight,
    MdKeyboardArrowDown
} from 'react-icons/md';
import { bankAccountsAPI, transactionsAPI } from 'services/api';

// --- YARDIMCI FONKSİYONLAR ---
const formatCurrency = (amount) => {
    return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(amount || 0);
};

const formatDate = (d) => d.toISOString().split('T')[0];

// --- SKELETON LOADERS ---
const SummarySkeleton = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 animate-pulse mt-6">
        <div className="h-40 w-full rounded-[20px] bg-gray-200 dark:bg-navy-700" />
        <div className="h-40 w-full rounded-[20px] bg-gray-200 dark:bg-navy-700" />
    </div>
);

const ListSkeleton = () => (
    <div className="flex flex-col gap-4 animate-pulse mt-6">
        <div className="h-16 w-full rounded-[20px] bg-gray-200 dark:bg-navy-700" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...Array(6)].map((_, i) => (
                <div key={i} className="h-32 w-full rounded-[20px] bg-gray-100 dark:bg-navy-900/50" />
            ))}
        </div>
    </div>
);

// --- MODAL COMPONENT (Banka Ekle/Düzenle) ---
const BankAccountModal = ({ isOpen, onClose, currentAccount, setCurrentAccount, onSave, saving }) => {
    if (!isOpen) return null;
    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-navy-900/40 backdrop-blur-sm p-4 transition-all animate-fadeIn">
            <div className="relative w-full max-w-lg bg-white dark:bg-navy-800 rounded-[24px] shadow-2xl overflow-hidden flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-100 dark:border-white/10">
                    <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-brand-500/10 text-brand-500 dark:bg-brand-400/10 dark:text-brand-400">
                            <MdAccountBalanceWallet className="text-xl" />
                        </div>
                        <h3 className="text-xl font-bold text-navy-700 dark:text-white">
                            {currentAccount.id ? 'Banka Hesabını Düzenle' : 'Yeni Banka Hesabı'}
                        </h3>
                    </div>
                    <button onClick={onClose} className="p-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-navy-700 rounded-full transition-all">
                        <MdClose className="text-xl" />
                    </button>
                </div>

                {/* Body */}
                <div className="p-6 flex flex-col gap-5">
                    <div>
                        <label className="text-sm font-bold text-navy-700 dark:text-white ml-1 mb-2 block">Banka/Hesap Adı *</label>
                        <input
                            required
                            value={currentAccount.name}
                            onChange={(e) => setCurrentAccount({...currentAccount, name: e.target.value})}
                            placeholder="Örn: Garanti BBVA Şirket"
                            className="flex h-12 w-full items-center justify-center rounded-xl border border-gray-200 bg-white/0 p-3 text-sm font-medium text-navy-700 outline-none focus:border-brand-500 dark:border-white/10 dark:text-white dark:focus:border-brand-400 transition-all"
                        />
                    </div>
                    <div>
                        <label className="text-sm font-bold text-navy-700 dark:text-white ml-1 mb-2 block">IBAN Numarası</label>
                        <input
                            value={currentAccount.iban}
                            onChange={(e) => setCurrentAccount({...currentAccount, iban: e.target.value})}
                            maxLength={34}
                            placeholder="TR..."
                            className="flex h-12 w-full items-center justify-center rounded-xl border border-gray-200 bg-white/0 p-3 text-sm font-mono font-medium text-navy-700 outline-none focus:border-brand-500 dark:border-white/10 dark:text-white dark:focus:border-brand-400 transition-all"
                        />
                        <span className="text-xs text-gray-400 ml-1 mt-1 block">TR ile başlamalıdır.</span>
                    </div>
                    <div>
                        <label className="text-sm font-bold text-navy-700 dark:text-white ml-1 mb-2 block">Pos Kesinti Oranı (%)</label>
                        <input
                            type="number"
                            step="0.01"
                            value={currentAccount.fee_percentage}
                            onChange={(e) => setCurrentAccount({...currentAccount, fee_percentage: e.target.value})}
                            placeholder="0.00"
                            className="flex h-12 w-full items-center justify-center rounded-xl border border-gray-200 bg-white/0 p-3 text-sm font-medium text-navy-700 outline-none focus:border-brand-500 dark:border-white/10 dark:text-white dark:focus:border-brand-400 transition-all"
                        />
                        <span className="text-xs text-gray-400 ml-1 mt-1 block">Her kredi kartı işleminde düşülecek yüzde.</span>
                    </div>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-100 dark:border-white/10 bg-gray-50 dark:bg-navy-800/50">
                    <button onClick={onClose} className="px-6 h-11 rounded-xl text-sm font-bold text-gray-600 hover:bg-gray-200 dark:text-gray-300 dark:hover:bg-navy-700 transition-all">
                        İptal
                    </button>
                    <button
                        onClick={onSave}
                        disabled={saving}
                        className="flex items-center justify-center gap-2 h-11 px-8 rounded-xl bg-brand-500 text-sm font-bold text-white shadow-lg shadow-brand-500/30 hover:bg-brand-600 disabled:opacity-60 transition-all active:scale-95"
                    >
                        {saving ? <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <><MdSave className="text-lg" /> Kaydet</>}
                    </button>
                </div>
            </div>
        </div>
    );
};


// --- ANA SAYFA COMPONENTİ ---
const AdminBankAccountManagement = () => {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);

    // Tarih Stateleri
    const [startDate, setStartDate] = useState(formatDate(firstDay));
    const [endDate, setEndDate] = useState(formatDate(today));
    const [quickFilter, setQuickFilter] = useState('thisMonth'); // Hızlı Filtre State'i

    const [loading, setLoading] = useState(true);
    const [accounts, setAccounts] = useState([]);
    const [accountStats, setAccountStats] = useState({});
    const [cashStats, setCashStats] = useState({ total_income: 0, total_expense: 0, net_profit: 0 });

    const [modalVisible, setModalVisible] = useState(false);
    const [currentAccount, setCurrentAccount] = useState({ id: null, name: '', iban: '', fee_percentage: '' });
    const [saving, setSaving] = useState(false);
    const [deletingId, setDeletingId] = useState(null);

    const [message, setMessage] = useState({ type: '', text: '' });

    // Pagination State
    const [currentPage, setCurrentPage] = useState(1);
    const PAGE_SIZE = 8;

    // --- HIZLI TARİH FİLTRESİ MANTIĞI ---
    const handleQuickFilterChange = (val) => {
        setQuickFilter(val);
        const end = new Date();
        let start = new Date();

        if (val === 'last7') {
            start.setDate(end.getDate() - 7);
        } else if (val === 'last30') {
            start.setDate(end.getDate() - 30);
        } else if (val === 'thisMonth') {
            start = new Date(end.getFullYear(), end.getMonth(), 1);
        } else if (val === 'thisYear') {
            start = new Date(end.getFullYear(), 0, 1);
        }

        setStartDate(formatDate(start));
        setEndDate(formatDate(end));
    };

    // --- API FETCH MANTIĞI (CoreUI Work-Logic) ---
    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            setMessage({ type: '', text: '' });

            const accRes = await bankAccountsAPI.getAll();
            let currentAccounts = [];

            if (accRes.success || accRes.bank_accounts) {
                currentAccounts = accRes.bank_accounts || accRes.data || accRes.accounts || [];
                setAccounts(currentAccounts);
            }

            const cashPromise = transactionsAPI.getSummary({
                start_date: startDate,
                end_date: endDate,
                payment_method: 'cash'
            });

            const bankPromises = currentAccounts.map(acc =>
                transactionsAPI.getSummary({
                    start_date: startDate,
                    end_date: endDate,
                    bank_account_id: acc.id,
                    payment_method: 'card'
                }).then(res => ({ id: acc.id, data: res.summary }))
            );

            const [cashRes, ...bankResults] = await Promise.all([cashPromise, ...bankPromises]);

            if (cashRes.success || cashRes.summary) {
                setCashStats(cashRes.summary || { total_income: 0, total_expense: 0, net_profit: 0 });
            }

            const newStats = {};
            bankResults.forEach(item => {
                newStats[item.id] = item.data || { total_income: 0, total_bank_fees: 0, net_profit: 0 };
            });
            setAccountStats(newStats);

        } catch (err) {
            console.error("Banka/Kasa verileri alınamadı:", err);
            setMessage({ type: 'danger', text: 'Veriler yüklenirken bir hata oluştu.' });
        } finally {
            setLoading(false);
        }
    }, [startDate, endDate]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // --- TOPLAM BANKA İSTATİSTİKLERİ (0 HATA ÇÖZÜMÜ BURASI) ---
    const totalBankStats = useMemo(() => {
        let income = 0;
        let expense = 0;
        let net = 0;

        Object.values(accountStats).forEach(s => {
            income += Number(s.total_income || 0);
            expense += Number(s.total_bank_fees || s.total_expense || 0);
            net += Number(s.net_profit || 0);
        });

        return { income, expense, net };
    }, [accountStats]);

    // --- İŞLEMLER ---
    const openModal = (account = null) => {
        if (account) {
            setCurrentAccount({
                id: account.id,
                name: account.name || '',
                iban: account.iban || '',
                fee_percentage: account.fee_percentage || ''
            });
        } else {
            setCurrentAccount({ id: null, name: '', iban: '', fee_percentage: '' });
        }
        setModalVisible(true);
    };

    const handleSaveAccount = async () => {
        if (!currentAccount.name) return setMessage({ type: 'danger', text: 'Hesap adı zorunludur.' });
        setSaving(true);
        try {
            if (currentAccount.id) {
                await bankAccountsAPI.update(currentAccount.id, currentAccount);
                setMessage({ type: 'success', text: 'Banka hesabı güncellendi.' });
            } else {
                await bankAccountsAPI.create(currentAccount);
                setMessage({ type: 'success', text: 'Yeni banka hesabı eklendi.' });
            }
            setModalVisible(false);
            fetchData();
        } catch (err) {
            setMessage({ type: 'danger', text: err.response?.data?.message || 'Kaydetme başarısız oldu.' });
        } finally {
            setSaving(false);
            setTimeout(() => setMessage({ type: '', text: '' }), 4000);
        }
    };

    const handleDeleteAccount = async (id) => {
        if (!window.confirm("Bu hesabı silmek istediğinize emin misiniz? (Bağlı işlemler etkilenebilir)")) return;
        setDeletingId(id);
        try {
            await bankAccountsAPI.delete(id);
            setMessage({ type: 'success', text: 'Hesap başarıyla silindi.' });
            fetchData();
        } catch (err) {
            setMessage({ type: 'danger', text: 'Silme işlemi başarısız oldu.' });
        } finally {
            setDeletingId(null);
            setTimeout(() => setMessage({ type: '', text: '' }), 4000);
        }
    };

    // --- SAYFALAMA HESAPLARI ---
    const totalPages = Math.max(1, Math.ceil(accounts.length / PAGE_SIZE));
    const currentAccounts = accounts.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

    return (
        <div className="mt-5 flex w-full flex-col gap-6 pb-20 animate-fadeIn">

            {/* 1. ÜST BİLGİ & GELİŞMİŞ TARİH FİLTRESİ */}
            <div className="flex flex-col xl:flex-row items-center justify-between gap-6 rounded-[20px] bg-white p-6 shadow-3xl shadow-shadow-500 dark:bg-navy-800 dark:shadow-none border border-gray-100 dark:border-white/10">
                <div className="flex items-center gap-4 w-full xl:w-auto">
                    <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-500/10 text-brand-500 dark:bg-brand-400/10 dark:text-brand-400">
                        <MdAccountBalanceWallet className="text-3xl" />
                    </div>
                    <div>
                        <h4 className="text-xl font-bold text-navy-700 dark:text-white">Banka & Kasa Yönetimi</h4>
                        <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                            İşletmenizin nakit ve banka hareketlerini yönetin.
                        </p>
                    </div>
                </div>

                {/* Hızlı Seçim ve Manuel Seçim Grubu */}
                <div className="flex flex-col md:flex-row items-center gap-3 w-full xl:w-auto bg-gray-50 dark:bg-navy-900 p-2 rounded-2xl border border-gray-100 dark:border-white/10">

                    {/* Hızlı Filtre Select */}
                    <div className="relative w-full md:w-40 border-r-0 md:border-r border-gray-200 dark:border-white/10 pr-0 md:pr-2">
                        <select
                            value={quickFilter}
                            onChange={(e) => handleQuickFilterChange(e.target.value)}
                            className="w-full h-10 appearance-none rounded-xl bg-white dark:bg-navy-800 px-3 text-sm font-bold text-navy-700 dark:text-white border border-gray-200 dark:border-white/10 outline-none cursor-pointer"
                        >
                            <option value="last7">Son 7 Gün</option>
                            <option value="last30">Son 30 Gün</option>
                            <option value="thisMonth">Bu Ay</option>
                            <option value="thisYear">Bu Yıl</option>
                            <option value="custom">Özel Tarih</option>
                        </select>
                        <MdKeyboardArrowDown className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                    </div>

                    <div className="flex items-center gap-2 w-full md:w-auto">
                        <div className="flex flex-col flex-1">
                            <span className="text-[10px] font-bold text-gray-400 uppercase ml-1 mb-0.5">Başlangıç</span>
                            <input
                                type="date"
                                value={startDate}
                                onChange={(e) => { setStartDate(e.target.value); setQuickFilter('custom'); }}
                                className="w-full h-10 rounded-xl bg-white dark:bg-navy-800 px-3 text-sm font-bold text-navy-700 dark:text-white border border-gray-200 dark:border-white/10 outline-none"
                            />
                        </div>
                        <div className="flex flex-col flex-1">
                            <span className="text-[10px] font-bold text-gray-400 uppercase ml-1 mb-0.5">Bitiş</span>
                            <input
                                type="date"
                                value={endDate}
                                onChange={(e) => { setEndDate(e.target.value); setQuickFilter('custom'); }}
                                className="w-full h-10 rounded-xl bg-white dark:bg-navy-800 px-3 text-sm font-bold text-navy-700 dark:text-white border border-gray-200 dark:border-white/10 outline-none"
                            />
                        </div>
                        <button
                            onClick={fetchData}
                            className="h-10 w-10 mt-[18px] flex-none flex items-center justify-center rounded-xl bg-brand-500 text-white shadow-md hover:bg-brand-600 transition-all active:scale-95"
                            title="Filtreyi Uygula"
                        >
                            <MdFilterList className="text-lg" />
                        </button>
                    </div>
                </div>
            </div>

            {/* Bildirimler */}
            {message.text && (
                <div className={`flex items-center gap-3 p-4 rounded-xl border ${message.type === 'danger' ? 'bg-red-50 border-red-200 text-red-600 dark:bg-red-500/10 dark:border-red-500/20 dark:text-red-400' : 'bg-green-50 border-green-200 text-green-600 dark:bg-green-500/10 dark:border-green-500/20 dark:text-green-400'}`}>
                    {message.type === 'danger' ? <MdInfoOutline className="text-xl flex-none" /> : <MdCheckCircle className="text-xl flex-none" />}
                    <span className="text-sm font-bold">{message.text}</span>
                </div>
            )}

            {/* 2. ÖZET KARTLARI (KASA VE GENEL BANKA) */}
            {loading ? <SummarySkeleton /> : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                    {/* NAKİT KASA ÖZETİ */}
                    <div className="flex flex-col justify-between p-6 rounded-[20px] bg-gradient-to-br from-green-500 to-green-600 text-white shadow-lg shadow-green-500/30 relative overflow-hidden hover:-translate-y-1 transition-transform">
                        <div className="absolute top-0 right-0 p-4 opacity-20"><MdWallet className="text-8xl" /></div>
                        <div>
                            <h5 className="text-lg font-bold flex items-center gap-2 mb-6">
                                <div className="p-2 rounded-xl bg-white/20 backdrop-blur-md"><MdWallet /></div> Nakit Kasa Özet
                            </h5>
                            <div className="space-y-4 relative z-10">
                                <div className="flex justify-between items-center">
                                    <span className="text-sm font-medium text-white/80">Toplam Nakit Gelir:</span>
                                    <span className="text-base font-bold">{formatCurrency(cashStats.total_income)}</span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-sm font-medium text-white/80">Toplam Nakit Gider:</span>
                                    <span className="text-base font-bold">{formatCurrency(cashStats.total_expense)}</span>
                                </div>
                                <div className="h-px w-full bg-white/20 my-2" />
                                <div className="flex justify-between items-center">
                                    <span className="text-base font-bold text-white/90">NET NAKİT:</span>
                                    <span className="text-2xl font-black">{formatCurrency(cashStats.net_profit)}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* TÜM BANKALAR ÖZETİ (0 HATA ÇÖZÜLDÜ) */}
                    <div className="flex flex-col justify-between p-6 rounded-[20px] bg-white dark:bg-navy-800 shadow-sm border border-gray-100 dark:border-white/10 relative overflow-hidden hover:-translate-y-1 transition-transform">
                        <div>
                            <h5 className="text-lg font-bold text-navy-700 dark:text-white flex items-center gap-2 mb-6">
                                <div className="p-2 rounded-xl bg-brand-50 text-brand-500 dark:bg-brand-400/10 dark:text-brand-400"><MdAccountBalanceWallet /></div>
                                Tüm Bankalar Özet
                            </h5>
                            <div className="space-y-4">
                                <div className="flex justify-between items-center">
                                    <span className="text-sm font-medium text-gray-500 dark:text-gray-400 flex items-center gap-1"><MdTrendingUp className="text-green-500" /> Toplam Gelir:</span>
                                    <span className="text-base font-bold text-green-500">{formatCurrency(totalBankStats.income)}</span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-sm font-medium text-gray-500 dark:text-gray-400 flex items-center gap-1"><MdTrendingDown className="text-red-500" /> Banka Gideri/Kesinti:</span>
                                    <span className="text-base font-bold text-red-500">{formatCurrency(totalBankStats.expense)}</span>
                                </div>
                                <div className="h-px w-full bg-gray-100 dark:bg-white/10 my-2" />
                                <div className="flex justify-between items-center">
                                    <span className="text-base font-bold text-navy-700 dark:text-white">NET BANKA DURUMU:</span>
                                    <span className="text-2xl font-black text-brand-500 dark:text-brand-400">{formatCurrency(totalBankStats.net)}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                </div>
            )}

            {/* 3. BANKA HESAPLARI LİSTESİ */}
            <div className="bg-white dark:bg-navy-800 rounded-[20px] shadow-sm border border-gray-100 dark:border-white/10 p-6">
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mb-6">
                    <h4 className="text-lg font-bold text-navy-700 dark:text-white">Kayıtlı Banka Hesapları Detayı</h4>
                    <button
                        onClick={() => openModal()}
                        className="flex items-center gap-2 h-10 px-5 rounded-xl bg-brand-500 text-white text-sm font-bold shadow-lg shadow-brand-500/30 hover:bg-brand-600 transition-all w-full sm:w-auto justify-center active:scale-95"
                    >
                        <MdAdd className="text-lg" /> Yeni Banka Ekle
                    </button>
                </div>

                {loading ? <ListSkeleton /> : accounts.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 opacity-60">
                        <MdAccountBalanceWallet className="text-6xl text-gray-300 dark:text-gray-600 mb-4" />
                        <h5 className="text-xl font-bold text-navy-700 dark:text-white">Hesap Bulunamadı</h5>
                        <p className="text-sm font-medium text-gray-500 mt-1">Sisteme kayıtlı banka hesabı yok.</p>
                    </div>
                ) : (
                    <>
                        {/* Masaüstü Tablo Görünümü */}
                        <div className="hidden lg:block overflow-x-auto">
                            <table className="w-full text-left">
                                <thead>
                                <tr className="border-b border-gray-100 dark:border-white/10 text-[11px] font-bold uppercase tracking-widest text-gray-400">
                                    <th className="py-3 pl-4">Banka / IBAN</th>
                                    <th className="py-3">Pos Kesinti</th>
                                    <th className="py-3 text-green-500">Gelir</th>
                                    <th className="py-3 text-red-500">Gider</th>
                                    <th className="py-3 text-brand-500">Net</th>
                                    <th className="py-3 pr-4 text-right">İşlemler</th>
                                </tr>
                                </thead>
                                <tbody>
                                {currentAccounts.map(account => {
                                    const stats = accountStats[account.id] || { total_income: 0, total_bank_fees: 0, net_profit: 0 };
                                    return (
                                        <tr key={account.id} className="border-b border-gray-50 dark:border-white/5 hover:bg-gray-50/50 dark:hover:bg-white/5 transition-colors">
                                            <td className="py-4 pl-4">
                                                <h6 className="text-sm font-bold text-navy-700 dark:text-white">{account.name}</h6>
                                                <p className="text-xs font-mono text-gray-500 dark:text-gray-400 mt-0.5">{account.iban || 'IBAN Yok'}</p>
                                            </td>
                                            <td className="py-4">
                          <span className="px-2 py-1 rounded bg-gray-100 dark:bg-navy-900 text-xs font-bold text-gray-600 dark:text-gray-300">
                            %{account.fee_percentage || '0.00'}
                          </span>
                                            </td>
                                            <td className="py-4 text-sm font-bold text-green-500">{formatCurrency(stats.total_income)}</td>
                                            <td className="py-4 text-sm font-bold text-red-500">{formatCurrency(stats.total_bank_fees || stats.total_expense)}</td>
                                            <td className="py-4 text-sm font-black text-brand-500 dark:text-brand-400">{formatCurrency(stats.net_profit)}</td>
                                            <td className="py-4 pr-4 text-right">
                                                <div className="flex items-center justify-end gap-2">
                                                    <button
                                                        onClick={() => openModal(account)}
                                                        className="h-8 w-8 flex items-center justify-center rounded-lg bg-gray-50 text-gray-500 hover:text-brand-500 hover:bg-brand-50 dark:bg-navy-900 dark:hover:bg-brand-400/10 dark:hover:text-brand-400 transition-all"
                                                    >
                                                        <MdEdit />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeleteAccount(account.id)}
                                                        disabled={deletingId === account.id}
                                                        className="h-8 w-8 flex items-center justify-center rounded-lg bg-red-50 text-red-500 hover:bg-red-500 hover:text-white dark:bg-red-500/10 dark:text-red-400 dark:hover:bg-red-500 dark:hover:text-white transition-all disabled:opacity-50"
                                                    >
                                                        {deletingId === account.id ? <div className="h-3 w-3 border-2 border-current border-t-transparent rounded-full animate-spin" /> : <MdDelete />}
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                                </tbody>
                            </table>
                        </div>

                        {/* Mobil Kart Görünümü */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:hidden gap-4">
                            {currentAccounts.map(account => {
                                const stats = accountStats[account.id] || { total_income: 0, total_bank_fees: 0, net_profit: 0 };
                                return (
                                    <div key={account.id} className="flex flex-col p-4 rounded-2xl border border-gray-100 dark:border-white/10 bg-gray-50/50 dark:bg-navy-900/30">
                                        <div className="flex justify-between items-start mb-3">
                                            <div>
                                                <h6 className="text-base font-bold text-navy-700 dark:text-white">{account.name}</h6>
                                                <p className="text-xs font-mono text-gray-500 mt-0.5">{account.iban || 'IBAN Yok'}</p>
                                            </div>
                                            <div className="flex gap-2">
                                                <button onClick={() => openModal(account)} className="p-2 bg-white dark:bg-navy-800 rounded-lg text-gray-500 shadow-sm"><MdEdit /></button>
                                                <button onClick={() => handleDeleteAccount(account.id)} disabled={deletingId === account.id} className="p-2 bg-red-50 dark:bg-red-500/10 rounded-lg text-red-500 shadow-sm">
                                                    {deletingId === account.id ? <div className="h-4 w-4 border-2 border-current border-t-transparent rounded-full animate-spin" /> : <MdDelete />}
                                                </button>
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-2 gap-2 mb-3">
                                            <div className="p-2 rounded-xl bg-white dark:bg-navy-800 border border-gray-100 dark:border-white/5">
                                                <span className="text-[10px] font-bold text-gray-400 uppercase block mb-1">Gelir</span>
                                                <span className="text-sm font-bold text-green-500">{formatCurrency(stats.total_income)}</span>
                                            </div>
                                            <div className="p-2 rounded-xl bg-white dark:bg-navy-800 border border-gray-100 dark:border-white/5">
                                                <span className="text-[10px] font-bold text-gray-400 uppercase block mb-1">Gider/Kesinti</span>
                                                <span className="text-sm font-bold text-red-500">{formatCurrency(stats.total_bank_fees || stats.total_expense)}</span>
                                            </div>
                                        </div>
                                        <div className="flex justify-between items-center p-3 rounded-xl bg-white dark:bg-navy-800 border border-gray-100 dark:border-white/5">
                                            <span className="text-xs font-bold text-navy-700 dark:text-white">NET DURUM</span>
                                            <span className="text-lg font-black text-brand-500 dark:text-brand-400">{formatCurrency(stats.net_profit)}</span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {/* SAYFALAMA */}
                        {totalPages > 1 && (
                            <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-100 dark:border-white/10">
                                <p className="text-sm font-medium text-gray-500">
                                    Toplam <span className="font-bold text-navy-700 dark:text-white">{accounts.length}</span> hesap.
                                </p>
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                        disabled={currentPage === 1}
                                        className="flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-500 disabled:opacity-40 hover:bg-gray-50 dark:border-white/10 dark:bg-navy-800 dark:hover:bg-navy-700 transition-all"
                                    >
                                        <MdKeyboardArrowLeft className="text-xl" />
                                    </button>
                                    <button
                                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                        disabled={currentPage === totalPages}
                                        className="flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-500 disabled:opacity-40 hover:bg-gray-50 dark:border-white/10 dark:bg-navy-800 dark:hover:bg-navy-700 transition-all"
                                    >
                                        <MdKeyboardArrowRight className="text-xl" />
                                    </button>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* MODAL */}
            <BankAccountModal
                isOpen={modalVisible}
                onClose={() => setModalVisible(false)}
                currentAccount={currentAccount}
                setCurrentAccount={setCurrentAccount}
                onSave={handleSaveAccount}
                saving={saving}
            />
        </div>
    );
};

export default AdminBankAccountManagement;